import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatIconChevronDown } from "blocks-nextjs/blocks-react/components/Icons/ChevronDown/ChevronDown";
import { CatIconChevronUp } from "blocks-nextjs/blocks-react/components/Icons/ChevronUp/ChevronUp";
import { CatIconSearch } from "blocks-nextjs/blocks-react/components/Icons/Search/Search";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { CatPagination } from "blocks-nextjs/blocks-react/components/Pagination/Pagination";
import { CatTable } from "blocks-nextjs/blocks-react/components/Table/Table";
import { CatTableBody } from "blocks-nextjs/blocks-react/components/TableBody/TableBody";
import { CatTableCell } from "blocks-nextjs/blocks-react/components/TableCell/TableCell";
import { CatTableHeader } from "blocks-nextjs/blocks-react/components/TableHeader/TableHeader";
import { CatTableHeaderCell } from "blocks-nextjs/blocks-react/components/TableHeaderCell/TableHeaderCell";
import { CatTableRow } from "blocks-nextjs/blocks-react/components/TableRow/TableRow";
import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import ErrorChargingOption from "../../assets/ErrorChargingOption.png";
import APIError from "../../components/APIError/APIError";
import DateRangeDropdown from "../../components/DateRangeDropdown/DateRangeDropdown";
import CustomDatePickerPopover from "../../components/GenerateLink/CustomDatePickerPopover";
import { Variant } from "../../components/GlobalNotification/GlobalNotification";
import ChargeHeaderDetails from "../../components/HeaderDetails/ChargeHeaderDetails";
import { useChargerDetailsQuery, useChargerTransactionListQuery, useChargerTransactionsCountQuery, useChargerTransactionsQuery } from "../../services/query/eflow-charger-dashboard-api.query";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { PropertiesForFiltering, RangeFilterType } from "../../swagger/caasService-v1";
import { formatDateWithTimezoneOffset } from "../../utils/dateFormatter";
import "./AssetPageDetail.css";
import { getAssetImageIcon, getCESTLocalTime, isLastReportedTimeStale, isTimeOnlyTimestamp } from "./ProjectDetails.helper";
const ChargeHistoryDetail = ({ chargerId, isRefreshComponent, refetchKey }) => {
    const [, setIsAlertsAvailable] = useState(false);
    const [, setAlertsCountForLast24hrs] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [cursor, setCursor] = useState("");
    const [skip, setSkip] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("endTime");
    const [sortDirection, setSortDirection] = useState("DESC");
    const [selectedDateRange, setSelectedDateRange] = useState("Last7Days");
    const [customFromDate, setCustomFromDate] = useState(null);
    const [customToDate, setCustomToDate] = useState(null);
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    // Store the previously selected range before opening custom date picker
    const [previousDateRange, setPreviousDateRange] = useState("Last7Days");
    const [chargeHistoryRefetchKey, setChargeHistoryRefetchKey] = useState(0);
    // Force transaction refetch when component mounts or refetchKey changes
    // We use refetchKey directly from parent instead of internal state management
    // This ensures APIs are called exactly once per visit
    // Static chargerId for testing
    const pageSizeOptions = [20, 30, 50];
    const { assetChargeActivityDetailsData, assetDetails, projectMetaData, projectName, streetAddress } = useProjectDetailsStore((state) => ({
        assetChargeActivityDetailsData: state.assetChargeActivityDetailsData,
        assetDetails: state.assetDetails,
        projectMetaData: state.projectMetaData,
        projectName: state.projectDetails?.projectName,
        streetAddress: state.projectDetails?.streetAddress
    }));
    const { setGlobalNotification } = useGlobalStore((state) => ({
        setGlobalNotification: state.setGlobalNotification
    }));
    const { assetId, timestamp = "" } = assetDetails || {};
    // Fetch charger details using the chargerId - we still fetch to get additional details like siteName
    const { data: chargerDetailsData } = useChargerDetailsQuery(chargerId || "", projectMetaData?.projectId || "", false, // Disabled: query will not run or fetch, but will return cached data if available
    undefined, false, // No refetch interval
    projectMetaData?.offset || "UTC+00:00");
    // New CaaS API returns chargerDetails (singular object), not chargers array
    const chargerDetails = chargerDetailsData?.chargerDetails;
    // Use the chargerId prop directly (already computed by parent) instead of trying to extract from cached data
    // The parent (ChargerPageDetail) already passes effectiveChargerAssetId as chargerId prop
    // Fallback order: prop > assetId (PRIMARY from search API) > chargerAssetId > assetSourceId
    const effectiveChargerAssetId = chargerId || chargerDetails?.assetId || chargerDetails?.chargerAssetId || chargerDetails?.assetSourceId;
    // Get timezone offset from project metadata for timezone-aware date formatting
    const timezoneOffset = useMemo(() => {
        return projectMetaData?.offset || "UTC+00:00";
    }, [projectMetaData?.offset]);
    // Calculate date range based on selection
    const { fromDate, toDate } = useMemo(() => {
        // If custom date range is selected and custom dates are set, use them
        if (selectedDateRange === "CustomDate" && customFromDate && customToDate) {
            return { fromDate: customFromDate, toDate: customToDate };
        }
        // Calculate today in the project's timezone to ensure consistency
        const getCurrentDateInTimezone = () => {
            const now = new Date();
            // Extract offset hours from format like "UTC+01:00" or "+09:00"
            const offsetStr = timezoneOffset.replace("UTC", "");
            const offsetMatch = offsetStr.match(/([+-])(\d{1,2}):?(\d{2})?/);
            if (offsetMatch) {
                const sign = offsetMatch[1] === "+" ? 1 : -1;
                const hours = parseInt(offsetMatch[2], 10);
                const minutes = parseInt(offsetMatch[3] || "0", 10);
                const totalOffsetMinutes = sign * (hours * 60 + minutes);
                // Get UTC time and apply project timezone offset
                const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
                const projectTime = new Date(utcTime + totalOffsetMinutes * 60000);
                return projectTime;
            }
            return now;
        };
        const today = getCurrentDateInTimezone();
        // Create a UTC date representing midnight in the project timezone
        // This ensures when we extract UTC components, we get the correct date
        const createProjectDate = (year, month, day, hour = 0, minute = 0, second = 0, ms = 0) => {
            // Create date string in ISO format for the project timezone
            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
            // Parse as UTC so getUTC* methods return the correct values
            return new Date(dateStr + "Z");
        };
        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth();
        const todayDay = today.getDate();
        // Start of today in project timezone (as UTC)
        const fromDateDefault = createProjectDate(todayYear, todayMonth, todayDay, 0, 0, 0, 0);
        // End of today in project timezone (as UTC)
        const toDateDefault = createProjectDate(todayYear, todayMonth, todayDay, 23, 59, 59, 999);
        let fromDate = fromDateDefault;
        let toDate = toDateDefault;
        switch (selectedDateRange) {
            case "Today":
                // Already set correctly
                break;
            case "Yesterday": {
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                fromDate = createProjectDate(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
                toDate = createProjectDate(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
                break;
            }
            case "Last7Days": {
                const weekAgo = new Date(today);
                weekAgo.setDate(today.getDate() - 6);
                fromDate = createProjectDate(weekAgo.getFullYear(), weekAgo.getMonth(), weekAgo.getDate(), 0, 0, 0, 0);
                break;
            }
            case "Last30Days": {
                const monthAgo = new Date(today);
                monthAgo.setDate(today.getDate() - 29);
                fromDate = createProjectDate(monthAgo.getFullYear(), monthAgo.getMonth(), monthAgo.getDate(), 0, 0, 0, 0);
                break;
            }
            case "CurrentWeek": {
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() - today.getDay()); // Sunday
                fromDate = createProjectDate(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate(), 0, 0, 0, 0);
                // Current week ends today
                break;
            }
            case "PreviousWeek": {
                const currentWeekStart = new Date(today);
                currentWeekStart.setDate(today.getDate() - today.getDay()); // Current week Sunday
                const prevWeekStart = new Date(currentWeekStart);
                prevWeekStart.setDate(currentWeekStart.getDate() - 7); // Previous week Sunday
                const prevWeekEnd = new Date(currentWeekStart);
                prevWeekEnd.setDate(currentWeekStart.getDate() - 1); // Previous week Saturday
                fromDate = createProjectDate(prevWeekStart.getFullYear(), prevWeekStart.getMonth(), prevWeekStart.getDate(), 0, 0, 0, 0);
                toDate = createProjectDate(prevWeekEnd.getFullYear(), prevWeekEnd.getMonth(), prevWeekEnd.getDate(), 23, 59, 59, 999);
                break;
            }
            case "CurrentMonth": {
                fromDate = createProjectDate(todayYear, todayMonth, 1, 0, 0, 0, 0);
                break;
            }
            case "LastMonth": {
                const lastMonthStart = new Date(todayYear, todayMonth - 1, 1);
                const lastMonthEnd = new Date(todayYear, todayMonth, 0); // Last day of previous month
                fromDate = createProjectDate(lastMonthStart.getFullYear(), lastMonthStart.getMonth(), lastMonthStart.getDate(), 0, 0, 0, 0);
                toDate = createProjectDate(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), lastMonthEnd.getDate(), 23, 59, 59, 999);
                break;
            }
            default: {
                // Default to Last7Days
                const weekAgo = new Date(today);
                weekAgo.setDate(today.getDate() - 6);
                fromDate = createProjectDate(weekAgo.getFullYear(), weekAgo.getMonth(), weekAgo.getDate(), 0, 0, 0, 0);
                break;
            }
        }
        return { fromDate, toDate };
    }, [selectedDateRange, customFromDate, customToDate, timezoneOffset]);
    // Memoize query parameters to prevent unnecessary re-renders and API calls
    // Format dates with timezone offset from project metadata
    const chargerTransactionsQueryParams = useMemo(() => {
        const formattedFromDate = formatDateWithTimezoneOffset(fromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(toDate, timezoneOffset);
        const params = {
            fromTime: formattedFromDate,
            limit: pageSize.toString(),
            orderBy: sortDirection || "DESC",
            skip: skip.toString(),
            sortBy: sortColumn || "endTime",
            toTime: formattedToDate
        };
        // Only include cursor if it has a value (use nextCursor as key name)
        if (cursor) {
            params.nextCursor = cursor;
        }
        return params;
    }, [cursor, fromDate, toDate, pageSize, skip, timezoneOffset, sortColumn, sortDirection]);
    //prepare request
    const chargerTransactionSearchRequest = useMemo(() => {
        const formattedFromDate = formatDateWithTimezoneOffset(fromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(toDate, timezoneOffset);
        const chargerTimeRange = {
            includeLeft: true,
            includeRight: false,
            left: formattedFromDate,
            right: formattedToDate
        };
        const chargerTimeRangeFilter = {
            propertyName: PropertiesForFiltering.TIMESTAMP,
            range: chargerTimeRange,
            type: RangeFilterType.TIME_RANGE
        };
        return { filters: [chargerTimeRangeFilter] };
    }, [fromDate, toDate, timezoneOffset]);
    const { isFetching: isChargerTransactionListFetching, isLoading: isChargerTransactionListLoading, refetch: refetchChargerTransactionList } = useChargerTransactionListQuery(chargerTransactionSearchRequest, {
        assetId: effectiveChargerAssetId || "",
        limit: 1000,
        orderBy: sortDirection || "DESC",
        projectId: projectMetaData?.projectId || "",
        sortBy: sortColumn || "endTime"
    });
    // Query parameters for count (include sort params to trigger refetch on sort change)
    const countQueryParams = useMemo(() => {
        const formattedFromDate = formatDateWithTimezoneOffset(fromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(toDate, timezoneOffset);
        return {
            fromTime: formattedFromDate,
            orderBy: sortDirection || "DESC",
            sortBy: sortColumn || "endTime",
            toTime: formattedToDate
        };
    }, [fromDate, toDate, sortColumn, sortDirection, timezoneOffset]);
    // Fetch transaction count
    const { data: countData } = useChargerTransactionsCountQuery(effectiveChargerAssetId || "", projectMetaData?.projectId || "", countQueryParams, !!effectiveChargerAssetId && !!projectMetaData?.projectId, refetchKey || chargeHistoryRefetchKey);
    const totalRecords = countData?.count || 0;
    // Fetch charger transactions using assetId and projectId
    const { data: chargerTransactionsData, isError: isTransactionsError, isLoading: isTransactionsLoading, refetch: refetchTransactions } = useChargerTransactionsQuery(effectiveChargerAssetId || "", projectMetaData?.projectId || "", chargerTransactionsQueryParams, !!effectiveChargerAssetId && !!projectMetaData?.projectId, refetchKey || chargeHistoryRefetchKey);
    const transactions = chargerTransactionsData?.transactions || [];
    // Filter transactions based on search term across all columns
    const filteredTransactions = useMemo(() => {
        // First, apply search filtering
        const searchLower = searchTerm.toLowerCase();
        const filtered = searchTerm.trim()
            ? transactions.filter((transaction) => {
                // Search across all visible columns
                const transactionId = (transaction.transactionId || "").toLowerCase();
                const connectorId = (transaction.connectorId || "").toString().toLowerCase();
                const startTime = (transaction.startTime || "").toLowerCase();
                const endTime = (transaction.endTime || "").toLowerCase();
                const macId = (transaction.macId || "").toLowerCase();
                const initialSoc = (transaction.initialSoc?.value || "").toString().toLowerCase();
                const finalSoc = (transaction.finalSoc?.value || "").toString().toLowerCase();
                // Format SOC values with % for percentage search (e.g., "28%")
                const initialSocWithPercent = transaction.initialSoc?.value
                    ? `${transaction.initialSoc.value}%`.toLowerCase()
                    : "";
                const finalSocWithPercent = transaction.finalSoc?.value
                    ? `${transaction.finalSoc.value}%`.toLowerCase()
                    : "";
                const energyDelivered = (transaction.energyDelivered?.value || "").toString().toLowerCase();
                const stopReason = (transaction.stopReason || "").toLowerCase();
                return (transactionId.includes(searchLower) ||
                    connectorId.includes(searchLower) ||
                    startTime.includes(searchLower) ||
                    endTime.includes(searchLower) ||
                    macId.includes(searchLower) ||
                    initialSoc.includes(searchLower) ||
                    finalSoc.includes(searchLower) ||
                    initialSocWithPercent.includes(searchLower) ||
                    finalSocWithPercent.includes(searchLower) ||
                    energyDelivered.includes(searchLower) ||
                    stopReason.includes(searchLower));
            })
            : transactions;
        // Sorting is now handled server-side via API parameters
        return filtered;
    }, [transactions, searchTerm]);
    // Get nextCursor from the response metadata (at top level, not per transaction)
    const nextCursor = chargerTransactionsData?.responseMetadata?.nextCursor;
    // Handle page change
    const onPageChange = (event) => {
        const newPageSize = event.detail.pageSize;
        const newPageNumber = event.detail.pageNumber;
        // If page size changed, reset to first page
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setCurrentPage(1);
            setCursor("");
            setSkip(0);
        }
        else {
            // Page number changed
            const oldPageNumber = currentPage;
            setCurrentPage(newPageNumber);
            // Calculate skip value based on page number
            const newSkip = (newPageNumber - 1) * pageSize;
            setSkip(newSkip);
            // If moving to next page and we have nextCursor, use it
            if (newPageNumber > oldPageNumber && nextCursor) {
                setCursor(nextCursor);
            }
            else if (newPageNumber === 1) {
                // Reset cursor when going back to first page
                setCursor("");
            }
        }
    };
    const formatLastReportedTime = (time) => {
        if (!time)
            return "";
        // Special handling for projects where API returns time-only format (e.g., "18:00:00")
        const isTimeOnlyFormat = isTimeOnlyTimestamp(time);
        if (isTimeOnlyFormat) {
            // Time-only format: Append today's date (using browser's date)
            // Note: For projects with timezone info, timezone abbreviation should be calculated from projectMetaData
            try {
                const now = new Date();
                const month = String(now.getMonth() + 1).padStart(2, "0");
                const day = String(now.getDate()).padStart(2, "0");
                const year = now.getFullYear();
                const timeFormatted = time.substring(0, 5);
                // Use timezone abbreviation if available, otherwise default to CEST
                const timezoneAbbr = projectMetaData?.timezone ? "Local Time" : "CEST";
                return `${month}/${day}/${year}; ${timeFormatted} ${timezoneAbbr}`;
            }
            catch (error) {
                console.error("Time-only timestamp construction error:", error);
                return time.substring(0, 5);
            }
        }
        // Standard ISO timestamp formatting
        return getCESTLocalTime(time, "MM/dd/yyyy; HH:mm") + " CEST";
    };
    const msToHMS = (duration) => {
        const minutes = Math.floor((duration / (1000 * 60)) % 60);
        const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
        return parseFloat(`${hours ? hours : "0"}.${minutes ? minutes : "0"}`);
    };
    // Calculate reporting status with timezone-aware staleness check
    const isTimeOnlyFormat = isTimeOnlyTimestamp(timestamp);
    const reportingStatus = !isTimeOnlyFormat && isLastReportedTimeStale(timestamp, projectMetaData?.timeZone || projectMetaData?.timezone)
        ? "Not Reporting"
        : "Reporting";
    const legendItems = [
        { color: "#8CBAFF", label: "Idle", value: 0 },
        { color: "#1AC69E", label: "Charging", value: 0 },
        { color: "#FB5A00", label: "Discharging", value: 0 },
        { color: "#777777", label: "Not Reported", value: 0 }
    ];
    const chartValues = {
        Charging: 0,
        Discharging: 0,
        Idle: 0,
        "Not Reported": 0
    };
    const assetTimelineStatus = [];
    const assetChargeActivityDetailsDataFormatted = assetChargeActivityDetailsData.map((item) => {
        const { activityEndTimeStamp = "", activityStartTimeStamp = "" } = item;
        const todayDate = new Date().toISOString().split("T")[0] + "T";
        if (activityStartTimeStamp && activityStartTimeStamp.indexOf("T") === -1) {
            item.activityStartTimeStamp = todayDate + activityStartTimeStamp;
        }
        if (activityEndTimeStamp && activityEndTimeStamp.indexOf("T") === -1) {
            item.activityEndTimeStamp = todayDate + activityEndTimeStamp;
        }
        return item;
    });
    if (assetChargeActivityDetailsDataFormatted && assetChargeActivityDetailsDataFormatted.length > 0) {
        const assetChargeActivitySorted = assetChargeActivityDetailsDataFormatted.sort((a, b) => {
            return new Date(b.activityStartTimeStamp).getTime() - new Date(a.activityStartTimeStamp).getTime();
        });
        assetChargeActivitySorted.forEach((assetChargeActivity) => {
            const { activity = "", activityEndTimeStamp = "", activityStartTimeStamp = "", endSoc = 0, startSoc = 0 } = assetChargeActivity;
            if (assetChargeActivity.assetId === assetId) {
                const activityStatus = {
                    batteryLevel: `${Math.round(startSoc)}% -> ${Math.round(endSoc)}%`,
                    details: "",
                    time: formatLastReportedTime(activityStartTimeStamp)
                };
                if (activity === "Charging" || activity === "AC_Charging" || activity === "DC_Charging") {
                    activityStatus.details = `Charging started at ${Math.round(startSoc)}%`; // activity timeline
                    chartValues.Charging += Math.abs(new Date(activityEndTimeStamp).getTime() - new Date(activityStartTimeStamp).getTime());
                }
                else if (activity === "Idle") {
                    activityStatus.details = activity; // activity timeline
                    chartValues.Idle += Math.abs(new Date(activityEndTimeStamp).getTime() - new Date(activityStartTimeStamp).getTime());
                }
                else {
                    activityStatus.details = `Charging stopped at ${Math.round(endSoc)}%`; // activity timeline
                    chartValues.Discharging += Math.abs(new Date(activityEndTimeStamp).getTime() - new Date(activityStartTimeStamp).getTime());
                }
                const totalDiffInSoc = Math.abs(startSoc - endSoc);
                const totalDiffInMs = Math.abs(new Date(activityEndTimeStamp).getTime() - new Date(activityStartTimeStamp).getTime());
                // Only show record if total activity is greater than 2 minutes and startsoc and endsoc are not same
                if (totalDiffInSoc > 0 && totalDiffInMs > 120000) {
                    assetTimelineStatus.push(activityStatus);
                }
            }
        });
        let totalHrsRecorder = 0, notReportedIndex = 3;
        legendItems.forEach((legendItem, index) => {
            const totalSeconds = chartValues[legendItem.label];
            legendItem.value = msToHMS(totalSeconds);
            totalHrsRecorder += totalSeconds;
            if (legendItem.label === "Not Reported") {
                notReportedIndex = index;
            }
        });
        const notReportedPowerHrs = (24 - msToHMS(totalHrsRecorder)).toFixed(2);
        legendItems[notReportedIndex].value = Number(notReportedPowerHrs);
    }
    useEffect(() => {
        //Wrapping this in setTimeout without any delay helps us finding the shadow-root element
        setTimeout(() => {
            //Finding the shadow-root element to modify the styles of the pagenation dropdown
            const alertsAccordionInnerPanel = document
                .querySelector(".alerts-accordion-panel")
                ?.shadowRoot.querySelector(".cat-c-accordion-panel__body-inner");
            alertsAccordionInnerPanel?.setAttribute("style", "padding:0px;");
        });
    });
    useEffect(() => {
        if (!isRefreshComponent) {
            setIsAlertsAvailable(false);
            setAlertsCountForLast24hrs(0);
            setShowCustomDatePicker(false);
        }
    }, [isRefreshComponent]);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const last7DaysStart = new Date(today);
    last7DaysStart.setDate(today.getDate() - 6);
    const last30DaysStart = new Date(today);
    last30DaysStart.setDate(today.getDate() - 29);
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay()); // Sunday
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6); // Saturday
    const previousWeekStart = new Date(currentWeekStart);
    previousWeekStart.setDate(currentWeekStart.getDate() - 7);
    const previousWeekEnd = new Date(currentWeekStart);
    previousWeekEnd.setDate(currentWeekStart.getDate() - 1);
    const handleSort = (column) => {
        // Reset pagination when sorting changes
        // setCurrentPage(1);
        // setCursor("");
        // setSkip(0);
        if (sortColumn === column) {
            // Toggle direction: DESC ↔ ASC
            if (sortDirection === "DESC") {
                setSortDirection("ASC");
            }
            else {
                setSortDirection("DESC");
            }
        }
        else {
            // When clicking a new column, start with DESC
            setSortColumn(column);
            setSortDirection("DESC");
        }
    };
    const getSortIcon = (column) => {
        if (sortColumn === column && sortDirection) {
            return (_jsx("span", { className: "sort-icon", children: sortDirection === "ASC" ? _jsx(CatIconChevronUp, { size: "sm" }) : _jsx(CatIconChevronDown, { size: "sm" }) }));
        }
        return (_jsx("span", { className: "sort-icon sort-icon-inactive", children: _jsx(CatIconChevronDown, { size: "sm" }) }));
    };
    const handleDownloadChargerTransactionList = async () => {
        const { data: chargerTransactionList, isError } = await refetchChargerTransactionList();
        if (isError) {
            setGlobalNotification({
                message: "Error: Failed to download CSV. Please try again later.",
                showNotification: true,
                type: Variant.error
            });
            return;
        }
        // Flatten the array of arrays into a single array
        const flattenData = chargerTransactionList.flat();
        // get all transactions into a single array
        const transactionList = flattenData.reduce((accumulator, current) => {
            if (current.transactions) {
                return accumulator.concat(current.transactions);
            }
            return accumulator;
        }, []);
        downloadCSV(transactionList);
    };
    const sanitizeCsvCell = (value) => {
        if (typeof value !== "string") {
            return value;
        }
        // Keep placeholder cells unchanged
        if (value === "-") {
            return value;
        }
        const trimmed = value.trimStart();
        // Prevent CSV/Excel formula injection by treating values that start with
        // =, +, -, or @ as plain text when the exported file is opened in a spreadsheet.
        return /^[=+\-@]/.test(trimmed) ? `'${value}` : value;
    };
    const downloadCSV = (chargerTransactionList) => {
        // Define CSV headers in desired display order
        const headers = [
            "Transaction ID",
            "Connector #",
            "Start Time",
            "End Time",
            "MAC ID",
            "Initial SOC",
            "Final SOC",
            "Energy Transferred (kWh)",
            "Stop Reason"
        ];
        // Prepare CSV data, preserving column order and mitigating CSV formula injection.
        const csvData = chargerTransactionList.map((row) => [
            sanitizeCsvCell(row.transactionId || "-"),
            sanitizeCsvCell(row.connectorId || "-"),
            sanitizeCsvCell(row.startTime || "-"),
            sanitizeCsvCell(row.endTime || "-"),
            sanitizeCsvCell(row.macId || "-"),
            sanitizeCsvCell(row.initialSoc?.value ? `${row.initialSoc.value}%` : "-"),
            sanitizeCsvCell(row.finalSoc?.value ? `${row.finalSoc.value}%` : "-"),
            sanitizeCsvCell(row.energyDelivered?.value || "-"),
            sanitizeCsvCell(row.stopReason || "-")
        ]);
        // Create worksheet from array of arrays (preserves column order)
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...csvData]);
        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Transaction History");
        // Generate filename with current date
        const baseName = assetDetails?.assetName?.trim() ? assetDetails.assetName : "transaction_history";
        const fileName = `${baseName}_${format(new Date(), "yyyy-MM-dd")}.csv`;
        // Download file
        XLSX.writeFile(workbook, fileName, { bookType: "csv" });
    };
    const handleDateRangeChange = useCallback((value) => {
        // When switching to CustomDate, don't change selectedDateRange yet
        // Just show the picker - the range will be set when Apply is clicked
        if (value === "CustomDate") {
            setPreviousDateRange(selectedDateRange);
            setShowCustomDatePicker(true);
        }
        else {
            // For other ranges, apply immediately
            setSelectedDateRange(value);
            setPreviousDateRange(value);
            setShowCustomDatePicker(false);
        }
        setChargeHistoryRefetchKey((prev) => prev + 1);
    }, [selectedDateRange]);
    const handleCustomDateCancel = useCallback(() => {
        setShowCustomDatePicker(false);
        // Revert to the previous date range selection
        setSelectedDateRange(previousDateRange);
    }, [previousDateRange]);
    const handleCustomDateApply = useCallback((startDate, endDate) => {
        // Parse the MM/dd/yyyy date strings and set custom dates in project timezone
        if (startDate && endDate) {
            // Helper to create a UTC date representing a specific time in the project timezone
            const createProjectDate = (year, month, day, hour = 0, minute = 0, second = 0, ms = 0) => {
                // Create date string in ISO format for the project timezone
                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
                // Parse as UTC so getUTC* methods return the correct values
                return new Date(dateStr + "Z");
            };
            // Parse MM/dd/yyyy format (e.g., "01/15/2026")
            const parseMMDDYYYY = (dateStr) => {
                const parts = dateStr.split("/");
                if (parts.length !== 3)
                    return null;
                return {
                    day: parseInt(parts[1], 10),
                    month: parseInt(parts[0], 10) - 1, // Month is 0-indexed
                    year: parseInt(parts[2], 10)
                };
            };
            const startParts = parseMMDDYYYY(startDate);
            const endParts = parseMMDDYYYY(endDate);
            if (startParts && endParts) {
                // Create dates in project timezone using the createProjectDate helper
                // Start date at 00:00:00.000
                const from = createProjectDate(startParts.year, startParts.month, startParts.day, 0, 0, 0, 0);
                // End date at 23:59:59.999
                const to = createProjectDate(endParts.year, endParts.month, endParts.day, 23, 59, 59, 999);
                // Update dates and set range to CustomDate
                setCustomFromDate(from);
                setCustomToDate(to);
                setSelectedDateRange("CustomDate");
                setPreviousDateRange("CustomDate");
            }
        }
        setShowCustomDatePicker(false);
    }, []);
    return (_jsxs("div", { className: "wrapper", "data-test": "asset-page-details", children: [_jsx(ChargeHeaderDetails, { activity: assetDetails?.activity, assetName: assetDetails?.assetName, capacity: assetDetails?.capacity, chargerId: chargerDetails?.assetSourceId, iconUrl: getAssetImageIcon(assetDetails), make: assetDetails?.assetMetadata?.make, name: assetDetails?.assetMetadata?.productFamily?.name, projectName: projectName || "Hansweert", reportingStatus: reportingStatus, serialNumber: assetDetails?.assetSerialNumber, siteName: chargerDetails?.siteName, streetAddress: streetAddress }), _jsx("div", { className: "flex w-full", "data-test": "asset-page-details-source", children: _jsx("div", { className: "flex flex-col w-full space-y-4", children: _jsxs("div", { children: [_jsxs("div", { className: "flex justify-between items-center gap-4 m-2", children: [_jsxs("div", { className: "relative flex-1 max-w-md", children: [_jsx("div", { className: "absolute inset-y-0 left-3 flex items-center pointer-events-none", children: _jsx(CatIconSearch, {}) }), _jsx("input", { className: "w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent", "data-test": "transaction-search-input", onChange: (e) => setSearchTerm(e.target.value), placeholder: "Find transactions", type: "text", value: searchTerm })] }), _jsxs("div", { children: [_jsx("div", { className: "flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3", children: _jsx(DateRangeDropdown, { isLastSevenDaysOptionOnly: false, onValueChange: handleDateRangeChange, selectedValue: selectedDateRange }) }), _jsx(CustomDatePickerPopover, { initialEndDate: customToDate
                                                    ? `${String(customToDate.getUTCMonth() + 1).padStart(2, "0")}/${String(customToDate.getUTCDate()).padStart(2, "0")}/${customToDate.getUTCFullYear()}`
                                                    : "", initialStartDate: customFromDate
                                                    ? `${String(customFromDate.getUTCMonth() + 1).padStart(2, "0")}/${String(customFromDate.getUTCDate()).padStart(2, "0")}/${customFromDate.getUTCFullYear()}`
                                                    : "", onApply: handleCustomDateApply, onCancel: handleCustomDateCancel, show: showCustomDatePicker, timezoneOffset: timezoneOffset })] }), _jsx("div", { children: _jsx(CatButton, { "data-test": "download-csv-button", disabled: isChargerTransactionListLoading || isChargerTransactionListFetching, onClick: handleDownloadChargerTransactionList, size: "sm", variant: "primary", children: _jsxs("span", { className: "inline-flex items-center gap-2", children: [(isChargerTransactionListLoading || isChargerTransactionListFetching) && (_jsx(CatLoadingIndicator, { small: true })), _jsx("span", { children: "Download CSV" })] }) }) })] }), _jsx("div", { className: "ml-4 mr-4", children: _jsxs("div", { children: [_jsx("div", { className: "transaction-table-wrapper", children: isTransactionsError ? (_jsx(APIError, { refreshHandler: () => refetchTransactions() })) : isTransactionsLoading ? (_jsx("div", { className: "flex justify-center items-center h-40", "data-test": "transactions-loading", children: _jsx(CatLoadingIndicator, {}) })) : filteredTransactions.length === 0 ? (_jsxs("div", { className: "flex flex-col justify-center items-center h-64 py-8", "data-test": "transactions-empty", children: [_jsx("img", { alt: "No transactions found", className: "w-48 h-48 mb-4", src: ErrorChargingOption }), _jsx("p", { className: "text-gray-500 text-lg font-medium", children: searchTerm
                                                            ? "No matching transactions found"
                                                            : "No transactions found" })] })) : (_jsxs(CatTable, { "data-test": "transactions-table", children: [_jsx(CatTableHeader, { "data-test": "transactions-table-header", children: _jsxs(CatTableRow, { className: "summary-table-row", "data-test": "transactions-header-row", children: [_jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "transaction-id-header", onClick: () => handleSort("transactionId"), children: ["Transaction ID ", getSortIcon("transactionId")] }), _jsxs(CatTableHeaderCell, { align: "center", className: "sortable-header", "data-test": "connector-number-header", onClick: () => handleSort("connectorId"), children: ["Connector # ", getSortIcon("connectorId")] }), _jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "start-time-header", onClick: () => handleSort("startTime"), children: ["Start Time ", getSortIcon("startTime")] }), _jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "end-time-header", onClick: () => handleSort("endTime"), children: ["End Time ", getSortIcon("endTime")] }), _jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "mac-id-header", onClick: () => handleSort("macId"), children: ["MAC ID ", getSortIcon("macId")] }), _jsxs(CatTableHeaderCell, { align: "center", className: "sortable-header", "data-test": "initial-soc-header", onClick: () => handleSort("initialSoc"), children: ["Initial SOC ", getSortIcon("initialSoc")] }), _jsxs(CatTableHeaderCell, { align: "center", className: "sortable-header", "data-test": "final-soc-header", onClick: () => handleSort("finalSoc"), children: ["Final SOC ", getSortIcon("finalSoc")] }), _jsx(CatTableHeaderCell, { align: "center", className: "sortable-header", "data-test": "energy-delivered-header", children: "Energy Transferred (kWh)" }), _jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "stop-reason-header", onClick: () => handleSort("stopReason"), children: ["Stop Reason ", getSortIcon("stopReason")] })] }) }), _jsx(CatTableBody, { "data-test": "transactions-table-body", children: filteredTransactions.map((rowCells, i) => (_jsxs(CatTableRow, { "data-test": `transaction-row-${i}`, children: [_jsx(CatTableCell, { "data-test": `transaction-id-${i}`, children: rowCells.transactionId || "-" }), _jsx(CatTableCell, { align: "center", "data-test": `connector-id-${i}`, children: rowCells.connectorId || "-" }), _jsx(CatTableCell, { "data-test": `start-time-${i}`, children: rowCells.startTime || "-" }), _jsx(CatTableCell, { "data-test": `end-time-${i}`, children: rowCells.endTime || "-" }), _jsx(CatTableCell, { "data-test": `mac-id-${i}`, children: rowCells.macId || "-" }), _jsx(CatTableCell, { align: "center", "data-test": `initial-soc-${i}`, children: rowCells.initialSoc?.value
                                                                        ? `${rowCells.initialSoc.value}%`
                                                                        : "-" }), _jsx(CatTableCell, { align: "center", "data-test": `final-soc-${i}`, children: rowCells.finalSoc?.value
                                                                        ? `${rowCells.finalSoc.value}%`
                                                                        : "-" }), _jsx(CatTableCell, { align: "center", "data-test": `energy-delivered-${i}`, children: rowCells.energyDelivered?.value || "-" }), _jsx(CatTableCell, { "data-test": `stop-reason-${i}`, children: rowCells.stopReason || "-" })] }, `row${i}${rowCells.transactionId}`))) })] })) }), filteredTransactions.length > 0 ? (_jsx("div", { className: "ml-4 mr-4", slot: "footer", children: _jsx(CatPagination, { currentItem: currentPage, "data-test": "transactions-pagination", onBlPageChange: onPageChange, pageSize: pageSize, pageSizeOptions: pageSizeOptions, totalRecords: searchTerm ? filteredTransactions.length : totalRecords }) })) : (_jsx("div", { className: "ml-4 mr-4 py-4", "data-test": "transactions-empty-footer", slot: "footer", children: _jsx("p", { className: "text-gray-700 text-sm font-medium", children: "Viewing 0-0 of 0" }) }))] }) })] }) }) })] }));
};
export default ChargeHistoryDetail;
